/**
 * Home Hero: video box reveal and scroll-driven animation.
 * Only runs if .home-hero__video-wrap and .home-hero__video-player exist.
 */
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

function lockHomeScroll() {
	document.documentElement.style.overflow = "hidden";
	document.body.style.overflow = "hidden";
}

function unlockHomeScroll() {
	document.documentElement.style.overflow = "";
	document.body.style.overflow = "";
}

const BORDER_RADIUS_START = 5;
const BORDER_RADIUS_END = 12;
const MOBILE_MQ = window.matchMedia("(max-width: 1024px)");

function isHomeHeroMobile() {
	return MOBILE_MQ.matches;
}

function formatTime(seconds) {
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return m + ":" + (s < 10 ? "0" : "") + s;
}

function initHomeHeroVideoControls(wrap) {
	const video = wrap.querySelector(".home-hero__video-element");
	const btnPlay = wrap.querySelector(".home-hero__video-btn--play");
	const btnMute = wrap.querySelector(".home-hero__video-btn--mute");
	const btnFullscreen = wrap.querySelector(".home-hero__video-btn--fullscreen");
	const progressInput = wrap.querySelector(".home-hero__video-progress");
	const progressFill = wrap.querySelector(".home-hero__video-progress-fill");
	const timeCurrent = wrap.querySelector(".home-hero__video-time-current");
	const timeDuration = wrap.querySelector(".home-hero__video-time-duration");
	if (!video || !btnPlay || !progressInput) return;

	function updateProgress() {
		const p = video.duration ? (100 * video.currentTime) / video.duration : 0;
		progressInput.value = p;
		if (progressFill) progressFill.style.setProperty("--video-progress", p + "%");
	}
	function updateTime() {
		if (timeCurrent) timeCurrent.textContent = formatTime(video.currentTime);
	}
	function setDuration() {
		if (timeDuration) timeDuration.textContent = formatTime(video.duration || 0);
	}

	btnPlay.addEventListener("click", () => {
		if (video.paused) {
			video.play();
			btnPlay.classList.add("home-hero__video-btn--playing");
		} else {
			video.pause();
			btnPlay.classList.remove("home-hero__video-btn--playing");
		}
	});
	video.addEventListener("play", () => btnPlay.classList.add("home-hero__video-btn--playing"));
	video.addEventListener("pause", () => btnPlay.classList.remove("home-hero__video-btn--playing"));

	if (btnMute) {
		btnMute.addEventListener("click", () => {
			video.muted = !video.muted;
			btnMute.classList.toggle("home-hero__video-btn--muted", video.muted);
		});
		btnMute.classList.add("home-hero__video-btn--muted");
	}

	if (btnFullscreen) {
		function getFullscreenElement() {
			return document.fullscreenElement || document.webkitFullscreenElement;
		}

		function isVideoFullscreen() {
			return (
				getFullscreenElement() === video ||
				Boolean(video.webkitDisplayingFullscreen)
			);
		}

		function updateFullscreenButton() {
			const active = isVideoFullscreen();
			btnFullscreen.classList.toggle("home-hero__video-btn--fullscreen-active", active);
			btnFullscreen.setAttribute("aria-label", active ? "Exit fullscreen" : "Enter fullscreen");
		}

		function enterNativeFullscreen() {
			// iOS Safari: native system video player (only API available on iOS).
			if (typeof video.webkitEnterFullscreen === "function") {
				video.webkitEnterFullscreen();
				return;
			}
			// Other browsers: fullscreen the <video> element (native player UI, not our controls bar).
			const request = video.requestFullscreen || video.webkitRequestFullscreen;
			if (request) {
				request.call(video);
			}
		}

		function exitNativeFullscreen() {
			if (video.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === "function") {
				video.webkitExitFullscreen();
				return;
			}
			const exit = document.exitFullscreen || document.webkitExitFullscreen;
			if (exit && getFullscreenElement()) {
				exit.call(document);
			}
		}

		btnFullscreen.addEventListener("click", () => {
			try {
				if (isVideoFullscreen()) {
					exitNativeFullscreen();
				} else {
					enterNativeFullscreen();
				}
			} catch (err) {
				// Fullscreen may be blocked by the browser or unsupported.
			}
			updateFullscreenButton();
		});

		document.addEventListener("fullscreenchange", updateFullscreenButton);
		document.addEventListener("webkitfullscreenchange", updateFullscreenButton);
		video.addEventListener("webkitbeginfullscreen", updateFullscreenButton);
		video.addEventListener("webkitendfullscreen", updateFullscreenButton);
		updateFullscreenButton();
	}

	progressInput.addEventListener("input", (e) => {
		const p = parseFloat(e.target.value, 10) / 100;
		if (video.duration) video.currentTime = p * video.duration;
	});
	progressInput.addEventListener("change", updateProgress);
	video.addEventListener("timeupdate", () => {
		updateProgress();
		updateTime();
	});
	video.addEventListener("loadedmetadata", setDuration);
	video.addEventListener("durationchange", setDuration);
	if (video.readyState >= 1) setDuration();
	updateTime();
	updateProgress();
	btnPlay.classList.toggle("home-hero__video-btn--playing", !video.paused);
}

const SMOOTH_PROGRESS_SPEED = 1;

function initHomeHeroScrollAnimation(wrap, videoPlayer) {
	let startRect = null;
	let placeholder = null;
	let completed = false;
	let targetProgress = 0;
	let smoothedProgress = 0;
	const wrapParent = wrap.parentNode;
	const videoPlayerParent = videoPlayer.parentNode;

	function fixToAbsoluteEnd() {
		if (completed) return;
		completed = true;

		if (placeholder && placeholder.parentNode) {
			placeholder.parentNode.removeChild(placeholder);
		}

		const container = videoPlayerParent;
		const pos = window.getComputedStyle(container).position;
		if (pos === "static") {
			container.style.position = "relative";
		}

		container.insertBefore(wrap, videoPlayer);

		const top = videoPlayer.offsetTop;
		const left = videoPlayer.offsetLeft;
		const width = videoPlayer.offsetWidth;
		const height = videoPlayer.offsetHeight;

		wrap.style.position = "absolute";
		wrap.style.top = top + "px";
		wrap.style.left = left + "px";
		wrap.style.width = width + "px";
		wrap.style.height = height + "px";
		wrap.style.margin = "0";
		wrap.style.zIndex = "10";
		wrap.style.transform = "none";
		wrap.style.transformOrigin = "";
		wrap.style.borderRadius = BORDER_RADIUS_END + "px";
		wrap.classList.add("home-hero__video-wrap--controls-visible");
		startRect = null;
		placeholder = null;
		initHomeHeroVideoControls(wrap);
	}

	function getStartRectView() {
		if (!startRect) return null;
		return {
			left: startRect.left - window.scrollX,
			top: startRect.top - window.scrollY,
			width: startRect.width,
			height: startRect.height,
		};
	}

	function setupFixedWrap() {
		if (startRect) return;
		const rect = wrap.getBoundingClientRect();
		startRect = {
			left: rect.left + window.scrollX,
			top: rect.top + window.scrollY,
			width: rect.width,
			height: rect.height,
		};
		const startView = getStartRectView();
		placeholder = document.createElement("span");
		placeholder.className = "home-hero__video-wrap-placeholder";
		placeholder.setAttribute("aria-hidden", "true");
		placeholder.style.cssText =
			"display:inline-block;width:" +
			startRect.width +
			"px;height:" +
			startRect.height +
			"px;vertical-align:middle;pointer-events:none;";
		wrapParent.insertBefore(placeholder, wrap);
		wrap.style.position = "fixed";
		wrap.style.margin = "0";
		wrap.style.zIndex = "10";
		wrap.style.transformOrigin = "0 0";
		wrap.style.borderRadius = BORDER_RADIUS_START + "px";
		const endRect = videoPlayer.getBoundingClientRect();
		wrap.style.left = endRect.left + "px";
		wrap.style.top = endRect.top + "px";
		wrap.style.width = endRect.width + "px";
		wrap.style.height = endRect.height + "px";
		wrap.style.transform =
			"translate(" +
			(startView.left - endRect.left) +
			"px, " +
			(startView.top - endRect.top) +
			"px) scale(" +
			startView.width / endRect.width +
			", " +
			startView.height / endRect.height +
			")";
	}

	function applyProgress(t) {
		if (!startRect) return;
		const startView = getStartRectView();
		if (!startView) return;
		const endRect = videoPlayer.getBoundingClientRect();
		wrap.style.left = endRect.left + "px";
		wrap.style.top = endRect.top + "px";
		wrap.style.width = endRect.width + "px";
		wrap.style.height = endRect.height + "px";
		const tx = (startView.left - endRect.left) * (1 - t);
		const ty = (startView.top - endRect.top) * (1 - t);
		const sx = startView.width / endRect.width + (1 - startView.width / endRect.width) * t;
		const sy = startView.height / endRect.height + (1 - startView.height / endRect.height) * t;
		wrap.style.transform = "translate(" + tx + "px, " + ty + "px) scale(" + sx + ", " + sy + ")";
		const visualRadius = BORDER_RADIUS_START + (BORDER_RADIUS_END - BORDER_RADIUS_START) * t;
		const scaleMin = Math.min(sx, sy);
		wrap.style.borderRadius = (scaleMin > 0.001 ? visualRadius / scaleMin : visualRadius) + "px";
		if (placeholder && placeholder.parentNode) {
			placeholder.style.width = startRect.width * (1 - t) + "px";
			placeholder.style.height = startRect.height * (1 - t) + "px";
		}
	}

	const st = ScrollTrigger.create({
		trigger: ".home-hero",
		start: "top top",
		endTrigger: ".home-hero__video-player",
		end: "center center",
		scrub: true,
		onEnter: setupFixedWrap,
		onUpdate: (self) => {
			if (completed) return;
			setupFixedWrap();
			if (!startRect) return;
			targetProgress = self.progress;
			if (targetProgress >= 1) fixToAbsoluteEnd();
		},
		onLeaveBack: () => {
			if (completed) return;
			if (placeholder && placeholder.parentNode) {
				placeholder.parentNode.removeChild(placeholder);
			}
			wrap.style.position = "";
			wrap.style.left = "";
			wrap.style.top = "";
			wrap.style.width = startRect.width + "px";
			wrap.style.height = startRect.height + "px";
			wrap.style.margin = "";
			wrap.style.zIndex = "";
			wrap.style.transform = "";
			wrap.style.transformOrigin = "";
			wrap.style.borderRadius = BORDER_RADIUS_START + "px";
			startRect = null;
			placeholder = null;
			targetProgress = 0;
			smoothedProgress = 0;
		},
	});

	gsap.ticker.add(tickerUpdate);
	function tickerUpdate() {
		if (completed || !startRect) return;
		smoothedProgress += (targetProgress - smoothedProgress) * SMOOTH_PROGRESS_SPEED;
		if (smoothedProgress >= 0.999) {
			smoothedProgress = 1;
			fixToAbsoluteEnd();
			return;
		}
		applyProgress(smoothedProgress);
	}

	function updateIfPastEnd() {
		if (completed) return;
		if (!startRect) return;
		if (st.progress >= 1) {
			fixToAbsoluteEnd();
		}
	}
	ScrollTrigger.addEventListener("scroll", updateIfPastEnd);
	ScrollTrigger.addEventListener("refresh", updateIfPastEnd);

	ScrollTrigger.refresh();
}

function startHomeHeroVideoPlayback(video) {
	const startPlaying = () => {
		video.play().catch(() => {});
	};
	if (video.readyState >= 3) startPlaying();
	else video.addEventListener("canplay", startPlaying, { once: true });
}

function initHomeHeroVideoMobile(wrap) {
	const box = wrap.querySelector(".home-hero__video-box");
	const video = box && box.querySelector(".home-hero__video-element");

	wrap.classList.add("home-hero__video-wrap--static", "home-hero__video-wrap--controls-visible");

	if (box) {
		box.style.opacity = "1";
	}

	if (video) {
		startHomeHeroVideoPlayback(video);
	}

	initHomeHeroVideoControls(wrap);
}

function initHomeHeroVideoDesktop(wrap, videoPlayer) {
	const anchor = document.querySelector(".home-hero__video-anchor");
	if (anchor) {
		anchor.appendChild(wrap);
	}

	lockHomeScroll();

	const box = wrap.querySelector(".home-hero__video-box");
	const video = box && box.querySelector(".home-hero__video-element");

	if (video) {
		startHomeHeroVideoPlayback(video);
	}

	const tl = gsap.timeline({ defaults: { ease: "power2.out" }, delay: 1 });
	tl.to(wrap, { width: 146, duration: 0.5 });
	tl.to(box, { opacity: 1, duration: 0.35 }, "-=0.2");
	tl.add(() => {
		initHomeHeroScrollAnimation(wrap, videoPlayer);
		unlockHomeScroll();
	});
}

export function initHomeHeroVideo() {
	const wrap = document.querySelector(".home-hero__video-wrap");
	const videoPlayer = document.querySelector(".home-hero__video-player");
	if (!wrap) return;

	if (isHomeHeroMobile()) {
		initHomeHeroVideoMobile(wrap);
		return;
	}

	if (!videoPlayer) return;

	initHomeHeroVideoDesktop(wrap, videoPlayer);
}
